/**
 * @fileoverview Servicio de renovación automática de tokens
 * Maneja refresh preventivo con cola de promesas para evitar race conditions
 * @author Omar Sánchez Martinez
 * @since 15/10/2025
 */

import axios, { type AxiosInstance } from 'axios';
import moment from 'moment';
import { useAuthStore } from '@/modules/auth/stores/auth.store';
import { REFRESH_BUFFER_MS, MAX_RETRIES, REFRESH_TIMEOUT_MS } from '../config';
import type { RefreshTokenResponse } from '../types';

/**
 * Cola de promesas de refresh para evitar múltiples llamadas simultáneas
 * Cuando múltiples peticiones detectan token expirado, todas esperan al mismo refresh
 */
let refreshPromise: Promise<string> | null = null;

/**
 * Contador de reintentos de refresh
 */
let refreshRetries = 0;

/**
 * Verifica si el token necesita ser renovado
 *
 * Criterios de renovación:
 * - No existe fecha de expiración
 * - Fecha de expiración es inválida
 * - Token expira en menos de REFRESH_BUFFER_MS (60 segundos)
 * - Token ya expiró
 *
 * @returns true si el token debe renovarse, false en caso contrario
 *
 * @example
 * if (shouldRefreshToken()) {
 *   await refreshToken();
 * }
 */
export const shouldRefreshToken = (): boolean => {
  const authStore = useAuthStore();
  const expirationDate = authStore.expirationDate;

  // No hay fecha de expiración
  if (!expirationDate) {
    return true;
  }

  // Crear momento de expiración desde timestamp en milisegundos
  const expirationMoment = moment(expirationDate, 'x');

  // Fecha de expiración inválida
  if (!expirationMoment.isValid()) {
    return true;
  }

  // Calcular tiempo hasta expiración usando moment
  const now = moment();
  const timeUntilExpiration = expirationMoment.diff(now, 'milliseconds');

  // Renovar si expira en menos del buffer o ya expiró
  return timeUntilExpiration < REFRESH_BUFFER_MS;
};

/**
 * Renueva el token de acceso llamando al endpoint de refresh
 * Usa una instancia separada de axios para evitar interceptors infinitos
 *
 * @returns Promise con el nuevo token de acceso
 * @throws Error si el refresh falla o el servidor retorna error
 *
 * @example
 * try {
 *   const newToken = await refreshToken();
 *   console.log('Token renovado:', newToken);
 * } catch (error) {
 *   console.error('Refresh falló:', error);
 *   // Usuario será deslogueado automáticamente
 * }
 */
export const refreshToken = async (): Promise<string> => {
  // Si ya hay un refresh en progreso, esperar a que termine
  if (refreshPromise) {
    return refreshPromise;
  }

  // Verificar número de reintentos
  if (refreshRetries >= MAX_RETRIES) {
    refreshRetries = 0;
    refreshPromise = null;
    throw new Error('Máximo de reintentos de refresh alcanzado');
  }

  // Crear promesa de refresh
  refreshPromise = (async () => {
    try {
      refreshRetries++;

      const authStore = useAuthStore();

      // Crear instancia de axios sin interceptors para evitar loops
      const refreshAxios: AxiosInstance = axios.create({
        baseURL: import.meta.env.VITE_MUNZEN_API_URL,
        timeout: REFRESH_TIMEOUT_MS,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authStore.token}`,
        },
      });

      // Llamar al endpoint de refresh
      const response = await refreshAxios.get<RefreshTokenResponse>('/users/refresh-token');

      const { token, expires_in } = response.data;

      // Actualizar tokens en el store usando moment
      const expirationMoment = moment().add(expires_in, 'seconds');
      const expirationTimestamp = expirationMoment.valueOf();

      authStore.token = token;
      authStore.expirationDate = expirationTimestamp;

      // Reset de reintentos y promesa
      refreshRetries = 0;
      refreshPromise = null;

      return token;
    } catch (error) {
      // Reset de promesa para permitir reintentos
      refreshPromise = null;

      // Log en desarrollo
      if (import.meta.env.DEV) {
        console.error('❌ Error en refresh token:', error);
      }

      // Si llegamos al máximo de reintentos o es un error 401, hacer logout
      const isUnauthorized =
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        (error as { response?: { status?: number } }).response?.status === 401;

      if (refreshRetries >= MAX_RETRIES || isUnauthorized) {
        refreshRetries = 0;
        const authStore = useAuthStore();
        await authStore.destroyLoginInfo();
        throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
      }

      throw error;
    }
  })();

  return refreshPromise;
};

/**
 * Limpia el estado del servicio de refresh
 * Útil para testing o cuando se hace logout manual
 *
 * @example
 * // Después de logout
 * clearRefreshState();
 */
export const clearRefreshState = (): void => {
  refreshPromise = null;
  refreshRetries = 0;
};
